import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check, Users } from 'lucide-react';
import type { Classroom } from '../../../accounts';
import { useClickOutside } from '../../../app/hooks/useClickOutside';
import { useEscapeKey } from '../../../app/hooks/useEscapeKey';
import { CoBadge } from './CoBadge';
interface ClassroomSwitcherProps {
  classrooms: Classroom[];
  selectedClassroomId: string | null;
  onSelectClassroom: (id: string | null) => void;
  /** Show a label like "Klas" before the value. Default: true */
  showLabel?: boolean;
  /** Visual size. Default: 'sm' */
  size?: 'sm' | 'md';
  /** Hide entirely when there's only one classroom (just shows the name as static label). Default: true */
  hideWhenSingle?: boolean;
  /** Active teacher account id — used to mark co-teacher classrooms with a 'Co' badge. */
  activeTeacherId?: string;
}
/**
 * Page-header classroom selector. Mirrors the sidebar dropdown but lives
 * inline next to a page title so teachers on tablet/mobile can switch
 * classrooms without opening the drawer.
 *
 * No Djay — this is operational UI (Zone 2 NO per Djay framework).
 */
export function ClassroomSwitcher({
  classrooms,
  selectedClassroomId,
  onSelectClassroom,
  showLabel = true,
  size = 'sm',
  hideWhenSingle = true,
  activeTeacherId
}: ClassroomSwitcherProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useClickOutside(containerRef, () => {
    if (open) setOpen(false);
  });
  useEscapeKey(() => {
    if (open) setOpen(false);
  });
  if (classrooms.length === 0) return null;
  const active = selectedClassroomId ? classrooms.find(c => c.id === selectedClassroomId) : null;
  const display = active ? active.name : 'Alle klassen';
  const learnerCount = active ? active.learnerIds.length : null;
  const isCoTeach = (c: Classroom) => !!activeTeacherId && c.teacherId !== activeTeacherId;
  // Static label fallback for single-classroom teachers
  if (hideWhenSingle && classrooms.length === 1) {
    const only = classrooms[0];
    return <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-600" data-id="element-9464" data-mp="1u4bp9smvivqb">
        <Users className="w-3.5 h-3.5 text-slate-400" data-id="element-9465" data-mp="1f8dwmkc5s9iu" />
        <span data-id="element-9466" data-mp="1k5w03v7epwjg">{only.name}</span>
        {isCoTeach(only) && <CoBadge data-id="element-9467" data-mp="27up2oaijb2n1" />}
        <span className="text-slate-400 font-medium" data-id="element-9468" data-mp="27uf7m8imu5ah">
          · {only.learnerIds.length}
        </span>
      </div>;
  }
  const buttonSize = size === 'md' ? 'px-4 py-2.5 text-sm' : 'px-3 py-2 text-xs';
  return <div ref={containerRef} className="relative inline-block" data-id="element-9469" data-mp="1y5nhm5x4tv27">
      <button type="button" onClick={() => setOpen(v => !v)} aria-haspopup="listbox" aria-expanded={open} className={`${buttonSize} inline-flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg font-bold text-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 shadow-sm`} data-id="element-9470" data-mp="233yuyqmmynp6">
        
        <Users className="w-3.5 h-3.5 text-slate-400" data-id="element-9471" data-mp="k3g3ofang50x" />
        {showLabel && <span className="text-slate-400 font-medium" data-id="element-9472" data-mp="f56843l0j2es">Klas:</span>}
        <span className="truncate max-w-[160px] text-slate-900" data-id="element-9473" data-mp="tuu913pa7z1z">{display}</span>
        {active && isCoTeach(active) && <CoBadge data-id="element-9474" data-mp="ovhc1q4p5blo" />}
        {learnerCount !== null && <span className="text-slate-400 font-medium" data-id="element-9475" data-mp="owspyiruvzqi">· {learnerCount}</span>}
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-9476" data-mp="17ho0xyrrzas" />
        
      </button>

      <AnimatePresence data-id="element-9477" data-mp="3seu6rhfawkst">
        {open && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.12
      }} className="absolute top-full right-0 mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-lg z-30 overflow-hidden py-1.5" role="listbox" aria-label="Klas selecteren" data-id="element-9478" data-mp="a7mhs5b1iz6h">
          
            <button onClick={() => {
          onSelectClassroom(null);
          setOpen(false);
        }} role="option" aria-selected={selectedClassroomId === null} className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${selectedClassroomId === null ? 'text-indigo-700 font-bold bg-indigo-50/50' : 'text-slate-700 font-medium'}`} data-id="element-9479" data-mp="2cvmrseyoed4d">
            
              <span data-id="element-9480" data-mp="2vsnwdyobib7s">Alle klassen</span>
              {selectedClassroomId === null && <Check className="w-4 h-4" data-id="element-9481" data-mp="2zv2euho2r8yj" />}
            </button>
            <div className="h-px bg-slate-100 my-1.5 mx-4" data-id="element-9482" data-mp="2zthh6j6gr9wg" />
            {classrooms.map(c => <button key={c.id} onClick={() => {
          onSelectClassroom(c.id);
          setOpen(false);
        }} role="option" aria-selected={selectedClassroomId === c.id} className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${selectedClassroomId === c.id ? 'text-indigo-700 font-bold bg-indigo-50/50' : 'text-slate-700 font-medium'}`} data-id="element-9483" data-mp="34mkgz91u3fl3">
            
                <div className="flex items-center gap-2 truncate min-w-0" data-id="element-9484" data-mp="17x98sb02aj0m">
                  <span className="truncate" data-id="element-9485" data-mp="12w24wn11i3z8">{c.name}</span>
                  {isCoTeach(c) && <CoBadge data-id="element-9486" data-mp="xwyyay5evpp5" />}
                  <span className="text-[10px] py-0.5 px-2 bg-slate-100 text-slate-500 rounded-md font-bold flex-shrink-0" data-id="element-9487" data-mp="xyabfokqy9sx">
                    {c.learnerIds.length}
                  </span>
                </div>
                {selectedClassroomId === c.id && <Check className="w-4 h-4 flex-shrink-0" data-id="element-9488" data-mp="1lnmjgl4c8btn" />}
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}